import { z } from "zod";
import { type EqualWithDiff, type Expect, isOneOf } from "@remi/ts-utils";

export const ProjectStatusValues = [
  "IN_PROGRESS",
  "ON_HOLD",
  "CANCELED",
  "DONE",
  "URGENT",
  "REOPENED",
  "CLOSED_LOST",
  "MERGED",
] as const;

export const ProjectStatusSchema = z.enum(ProjectStatusValues);
export type ProjectStatus = (typeof ProjectStatusValues)[number];
export const ProjectStatus = ProjectStatusSchema.enum;

export const ProjectStatusPositions: Record<ProjectStatus, number> = {
  [ProjectStatus.URGENT]: 0,
  [ProjectStatus.IN_PROGRESS]: 1,
  [ProjectStatus.REOPENED]: 2,
  [ProjectStatus.ON_HOLD]: 3,
  [ProjectStatus.DONE]: 4,
  [ProjectStatus.CANCELED]: 5,
  [ProjectStatus.CLOSED_LOST]: 6,
  [ProjectStatus.MERGED]: 7,
} as const;

export const ProjectStatusLabels = {
  [ProjectStatus.IN_PROGRESS]: "In Progress",
  [ProjectStatus.ON_HOLD]: "On Hold",
  [ProjectStatus.CANCELED]: "Canceled",
  [ProjectStatus.DONE]: "Done",
  [ProjectStatus.URGENT]: "Urgent",
  [ProjectStatus.REOPENED]: "Reopened",
  [ProjectStatus.CLOSED_LOST]: "Closed Lost",
  [ProjectStatus.MERGED]: "Merged",
} as const;

export const CanceledProjectStatuses = [
  ProjectStatus.CANCELED,
  ProjectStatus.CLOSED_LOST,
  ProjectStatus.MERGED,
] as const satisfies ProjectStatus[];

export const ActiveProjectStatuses = [
  ProjectStatus.IN_PROGRESS,
  ProjectStatus.ON_HOLD,
  ProjectStatus.REOPENED,
  ProjectStatus.URGENT,
] as const satisfies ProjectStatus[];

export const NonActiveProjectStatuses = [
  ProjectStatus.DONE,
  ...CanceledProjectStatuses,
] as const satisfies ProjectStatus[];

// Type test to ensure all statuses are in active or non-active
type _ActiveAndNonActiveProjectStatusesTest = Expect<
  EqualWithDiff<ProjectStatus, (typeof ActiveProjectStatuses)[number] | (typeof NonActiveProjectStatuses)[number]>
>;

// CLOSED_LOST and MERGED are considered canceled
export const projectIsCanceled = (status: ProjectStatus) => {
  return isOneOf(status, CanceledProjectStatuses);
};

export const projectIsClosed = (status: ProjectStatus) => {
  return isOneOf(status, NonActiveProjectStatuses);
};

export const CancellationReasonValues = [
  "CANCELLED_BY_CORPORATE_PARTNER",
  "DUPLICATE",
  "COMPETITOR_PRICING",
  "WAITING_TO_DO_PROJECT",
  "BAD_HOMEOWNER_EXPERIENCE",
  "NO_ROOF_WORK_NEEDED",
  "SOW_NOT_SUPPORTED",
  "PATCH_WORK",
  "GROUND_EQUIPMENT_ONLY",
  "HOMEOWNER_UNRESPONSIVE",
  "HOMEOWNER_COMMITTED_TO_ANOTHER_CONTRACTOR",
  "CREATED_BY_MISTAKE",
  "ON_HOLD_FOR_60_DAYS",
  "UNKNOWN_CANCELLATION_REASON",
] as const;

export const CancellationReasonSchema = z.enum(CancellationReasonValues);
export const CancellationReason = CancellationReasonSchema.enum;
export type CancellationReason = z.infer<typeof CancellationReasonSchema>;

export const CancellationReasonsDisplayName: Record<CancellationReason, string> = {
  CANCELLED_BY_CORPORATE_PARTNER: "Cancelled by corporate partner",
  DUPLICATE: "Duplicate",
  COMPETITOR_PRICING: "Competitor pricing",
  WAITING_TO_DO_PROJECT: "Waiting to do project",
  BAD_HOMEOWNER_EXPERIENCE: "Bad homeowner experience",
  NO_ROOF_WORK_NEEDED: "No roof work needed",
  SOW_NOT_SUPPORTED: "SOW not supported",
  PATCH_WORK: "Patch work",
  GROUND_EQUIPMENT_ONLY: "Ground equipment only",
  HOMEOWNER_UNRESPONSIVE: "Homeowner unresponsive",
  HOMEOWNER_COMMITTED_TO_ANOTHER_CONTRACTOR: "Homeowner committed to another contractor",
  CREATED_BY_MISTAKE: "Created by mistake",
  ON_HOLD_FOR_60_DAYS: "Project was on hold for 60 days",
  UNKNOWN_CANCELLATION_REASON: "Unknown cancellation reason",
} as const;
